import type { PasswordValidationStatus } from "firebase/auth";

export const ACCOUNT_LOGIN_URL = "https://tu-conferences.taibahvalley.com.sa/";
export const ACCOUNT_ACTION_PATH = "/account/action";
const BUILTIN_MODES = new Set([
  "verifyEmail",
  "recoverEmail",
  "verifyAndChangeEmail",
  "revertSecondFactorAddition",
  "signIn",
]);

export type AccountAction =
  | { kind: "password"; code: string }
  | { kind: "builtin"; url: string }
  | { kind: "invalid" };

// Never trust continueUrl, apiKey, or a host supplied by the incoming email URL.
export function parseAccountAction(search: string): AccountAction {
  if (search.length > 8192) return { kind: "invalid" };
  const params = new URLSearchParams(search);
  const mode = params.get("mode");
  const code = params.get("oobCode");
  if (
    params.getAll("mode").length !== 1 ||
    params.getAll("oobCode").length !== 1 ||
    !code ||
    !/^[A-Za-z0-9_-]{16,2048}$/.test(code)
  )
    return { kind: "invalid" };
  if (mode === "resetPassword") return { kind: "password", code };
  if (!mode || !BUILTIN_MODES.has(mode)) return { kind: "invalid" };
  const target = new URL("/__/auth/action", ACCOUNT_LOGIN_URL);
  target.searchParams.set("mode", mode);
  target.searchParams.set("oobCode", code);
  target.searchParams.set("lang", "ar");
  target.searchParams.set("continueUrl", ACCOUNT_LOGIN_URL);
  // The caller supplies this application's configured key, never the query key.
  return { kind: "builtin", url: target.href };
}

export interface PasswordActionGateway {
  verify(code: string): Promise<void>;
  validate(password: string): Promise<PasswordValidationStatus>;
  confirm(code: string, password: string): Promise<void>;
}
export interface PasswordActionState {
  view: "loading" | "ready" | "invalid" | "success";
  validation: PasswordValidationStatus | null;
  busy: boolean;
  validating: boolean;
  matches: boolean;
  hasConfirmation: boolean;
  error: "policy" | "save" | null;
}

export class PasswordActionController {
  #state: PasswordActionState = {
    view: "loading",
    validation: null,
    busy: false,
    validating: false,
    matches: false,
    hasConfirmation: false,
    error: null,
  };
  #listeners = new Set<() => void>();
  #password = "";
  #confirmation = "";
  #code: string;
  #revision = 0;
  #load: Promise<void> | null = null;
  #gateway: PasswordActionGateway;
  constructor(code: string, gateway: PasswordActionGateway) {
    this.#code = code;
    this.#gateway = gateway;
  }
  getSnapshot = () => this.#state;
  subscribe = (listener: () => void) => {
    this.#listeners.add(listener);
    return () => {
      this.#listeners.delete(listener);
    };
  };
  #publish(update: Partial<PasswordActionState>) {
    this.#state = Object.freeze({ ...this.#state, ...update });
    for (const listener of this.#listeners) listener();
  }
  load() {
    return (this.#load ??= this.#initialize());
  }
  async #initialize() {
    try {
      const [, validation] = await Promise.all([
        this.#gateway.verify(this.#code),
        this.#gateway.validate(""),
      ]);
      this.#publish({ view: "ready", validation });
    } catch {
      this.#publish({ view: "invalid" });
    }
  }
  async setPassword(password: string) {
    if (this.#state.view !== "ready" || this.#state.busy) return;
    this.#password = password;
    const revision = ++this.#revision;
    this.#publish({
      validating: true,
      error: null,
      matches: password.length > 0 && password === this.#confirmation,
    });
    try {
      const validation = await this.#gateway.validate(password);
      if (revision === this.#revision && this.#state.view === "ready")
        this.#publish({ validation, validating: false });
    } catch {
      if (revision === this.#revision)
        this.#publish({ error: "policy", validation: null, validating: false });
    }
  }
  setConfirmation(value: string) {
    if (this.#state.view !== "ready" || this.#state.busy) return;
    this.#confirmation = value;
    this.#publish({
      hasConfirmation: value.length > 0,
      matches: value.length > 0 && value === this.#password,
    });
  }
  async save() {
    if (
      this.#state.view !== "ready" ||
      this.#state.busy ||
      this.#state.validating ||
      !this.#state.validation?.isValid ||
      !this.#state.matches
    )
      return;
    this.#publish({ busy: true, error: null });
    try {
      await this.#gateway.confirm(this.#code, this.#password);
      this.#password = "";
      this.#confirmation = "";
      this.#code = "";
      ++this.#revision;
      this.#publish({
        view: "success",
        busy: false,
        validation: null,
        matches: false,
        hasConfirmation: false,
      });
    } catch {
      this.#publish({ busy: false, error: "save" });
    }
  }
}

export function passwordCriteria(status: PasswordValidationStatus) {
  const options = status.passwordPolicy.customStrengthOptions;
  const criteria: {
    key: string;
    label: string;
    sample?: string;
    met: boolean;
  }[] = [];
  if (options.minPasswordLength !== undefined)
    criteria.push({
      key: "min",
      label: `${options.minPasswordLength} أحرف على الأقل`,
      met: status.meetsMinPasswordLength === true,
    });
  if (options.maxPasswordLength !== undefined)
    criteria.push({
      key: "max",
      label: `${options.maxPasswordLength} حرفًا كحد أقصى`,
      met: status.meetsMaxPasswordLength === true,
    });
  if (options.containsLowercaseLetter)
    criteria.push({
      key: "lower",
      label: "حرف إنجليزي صغير",
      sample: "a–z",
      met: status.containsLowercaseLetter === true,
    });
  if (options.containsUppercaseLetter)
    criteria.push({
      key: "upper",
      label: "حرف إنجليزي كبير",
      sample: "A–Z",
      met: status.containsUppercaseLetter === true,
    });
  if (options.containsNumericCharacter)
    criteria.push({
      key: "number",
      label: "رقم واحد على الأقل",
      sample: "0–9",
      met: status.containsNumericCharacter === true,
    });
  if (options.containsNonAlphanumericCharacter)
    criteria.push({
      key: "symbol",
      label: "رمز خاص من",
      sample: status.passwordPolicy.allowedNonAlphanumericCharacters,
      met: status.containsNonAlphanumericCharacter === true,
    });
  return criteria;
}
